import React, { useState } from 'react';
import { Box, Paper, Stack, Text, Title } from '@mantine/core';
import { PersonReferenceField, type PersonReferenceSelection } from './PersonReferenceField';
import { JournalEntriesView } from './JournalEntriesView';
import { getSingularLabel } from '../../utils/pluralizationUtils';

export interface EntityActorSearchTabProps {
    entityType: string;
}

/**
 * Actor-based journal search: select a user, then list journal entries for this entity type
 * where Actor matches (deduplicated by target entity id).
 */
export const EntityActorSearchTab: React.FC<EntityActorSearchTabProps> = ({ entityType }) => {
    const [selectedUser, setSelectedUser] = useState<PersonReferenceSelection | null>(null);
    const entityLabel = getSingularLabel(entityType);

    return (
        <Box w="100%">
            <Paper withBorder p="md" radius="md" mb="md">
                <Stack gap="md">
                    <Box>
                        <Title order={5} mb={4}>
                            {entityLabel} by author
                        </Title>
                        <Text size="sm" c="dimmed">
                            Step 1 — select a user. Step 2 loads journal entries for {entityLabel.toLowerCase()} they
                            changed (one row per {entityLabel.toLowerCase()}). Use the entity type filter in the user
                            browser to narrow person types.
                        </Text>
                    </Box>

                    <PersonReferenceField
                        label="User"
                        value={selectedUser}
                        onChange={setSelectedUser}
                        description="Search all users; narrow by person type in the selection dialog."
                    />
                </Stack>
            </Paper>

            {selectedUser ? (
                <Box>
                    <Title order={5} mb="xs">
                        Step 2 — {entityLabel} by {selectedUser.displayName}
                    </Title>
                    <Text size="sm" c="dimmed" mb="md">
                        Journal entries where Actor is {selectedUser.id}, deduplicated by {entityLabel.toLowerCase()}{' '}
                        (TargetId).
                    </Text>
                    <JournalEntriesView
                        entityType={entityType}
                        actorId={selectedUser.id}
                        deduplicateByTargetId
                    />
                </Box>
            ) : (
                <Text size="sm" c="dimmed">
                    Select a user to search {entityLabel.toLowerCase()} journal entries.
                </Text>
            )}
        </Box>
    );
};
